import React, { useEffect, useMemo, useState } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./addCityHead.css";
import {
  editCityHead,
  getFranchiseManagerDetail,
  getFranchiseCityList,
  getFranchiseLocalityList,
  getFranchiseProjectListScoped,
} from "../../Redux/Actions/franchiseManagerAction";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import SearchableMultiSelect from "../../Global Functions/SearchableMultiSelect";

const EditCityHead = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { franchise_id, city_head_id } = useParams();

  const {
    franchiseManagerDetail,
    detailLoading,
    cityList: rawCityList,
    localityList: rawLocalityList,
    projectList: rawProjectList,
    cityHeadEditing = false,
  } = useSelector((state) => state.franchiseManager || {});

  const cities = Array.isArray(rawCityList) ? rawCityList : [];
  const localities = Array.isArray(rawLocalityList) ? rawLocalityList : [];
  const projects = Array.isArray(rawProjectList) ? rawProjectList : [];

  const [errors, setErrors] = useState({});
  const [hydrated, setHydrated] = useState(false);
  const [cityHeadData, setCityHeadData] = useState({
    name: "",
    email: "",
    country_code: "+91",
    contact_number: "",
    city: [],
    locality: [],
    project: [],
  });

  // Fetch the city head's own record first — its franchise_id is the parent
  // whose city/locality/project pool we need to scope the dropdowns against.
  useEffect(() => {
    if (city_head_id) {
      dispatch(getFranchiseManagerDetail(city_head_id));
    }
  }, [city_head_id, dispatch]);

  // Once we know the record, pull the parent's scoped lists using ITS
  // franchise_id (the root franchise manager) — same source addCityHead uses.
  useEffect(() => {
    const parentId = franchiseManagerDetail?.franchise_id?._id || franchiseManagerDetail?.franchise_id || franchise_id;
    if (parentId) {
      dispatch(getFranchiseCityList({ franchise_id: parentId }, false));
      dispatch(getFranchiseLocalityList({ franchise_id: parentId }, false));
      dispatch(getFranchiseProjectListScoped({ franchise_id: parentId }, false));
    }
  }, [franchiseManagerDetail, franchise_id, dispatch]);

  // Pre-fill the form once the detail arrives (only once, so user edits aren't clobbered)
  useEffect(() => {
    if (franchiseManagerDetail && franchiseManagerDetail._id === city_head_id && !hydrated) {
      setCityHeadData({
        name: franchiseManagerDetail.name || "",
        email: franchiseManagerDetail.email || "",
        country_code: franchiseManagerDetail.country_code || "+91",
        contact_number: franchiseManagerDetail.contact_number || "",
        city: (franchiseManagerDetail.city || []).map((c) => c._id || c),
        locality: (franchiseManagerDetail.locality || []).map((l) => l._id || l),
        project: (franchiseManagerDetail.project || []).map((p) => p._id || p),
      });
      setHydrated(true);
    }
  }, [franchiseManagerDetail, city_head_id, hydrated]);

  const cityOptions = useMemo(
    () => cities.map((c) => ({ value: c._id, label: c.city_name })),
    [cities]
  );

  const localityOptions = useMemo(() => {
    return localities
      .filter((loc) => cityHeadData.city.includes(loc.city))
      .map((loc) => ({
        value: loc._id,
        label: loc.name,
        hint: loc.available === false ? "Taken" : undefined,
        // don't lock out a locality this city head already holds
        disabled: loc.available === false && !franchiseManagerDetail?.locality?.some((l) => (l._id || l) === loc._id),
      }));
  }, [localities, cityHeadData.city, franchiseManagerDetail]);

  const projectOptions = useMemo(() => {
    return projects
      .filter((p) => {
        const projLocalityIds = (p.localities || []).map((l) => l._id || l);
        return projLocalityIds.some((lid) => cityHeadData.locality.includes(lid));
      })
      .map((p) => ({
        value: p._id,
        label: p.project_name,
        group: p.city?.city_name,
        hint: p.available === false ? "Taken" : undefined,
        disabled: p.available === false && !franchiseManagerDetail?.project?.some((pr) => (pr._id || pr) === p._id),
      }));
  }, [projects, cityHeadData.locality, franchiseManagerDetail]);

  const clearError = (field) => {
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setCityHeadData((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const handleCityChange = (values) => {
    setCityHeadData((prev) => ({
      ...prev,
      city: values,
      locality: prev.locality.filter((locId) => {
        const loc = localities.find((l) => l._id === locId);
        return loc && values.includes(loc.city);
      }),
      project: [],
    }));
    clearError("city");
  };

  const handleLocalityChange = (values) => {
    setCityHeadData((prev) => {
      const autoSelected = projects
        .filter((p) => {
          if (p.available === false) return false;
          const projLocalityIds = (p.localities || []).map((l) => l._id || l);
          return projLocalityIds.some((lid) => values.includes(lid));
        })
        .map((p) => p._id);

      const kept = prev.project.filter((projId) => {
        const proj = projects.find((p) => p._id === projId);
        if (!proj) return false;
        const projLocalityIds = (proj.localities || []).map((l) => l._id || l);
        return projLocalityIds.some((lid) => values.includes(lid));
      });

      return {
        ...prev,
        locality: values,
        project: values.length === 0 ? [] : Array.from(new Set([...kept, ...autoSelected])),
      };
    });
    clearError("locality");
  };

  const handleProjectChange = (values) => {
    setCityHeadData((prev) => ({ ...prev, project: values }));
    clearError("project");
  };

  const validate = () => {
    const newErrors = {};
    if (!cityHeadData.name.trim()) newErrors.name = "Name is required";
    if (!cityHeadData.email.trim()) newErrors.email = "Email is required";
    if (!cityHeadData.contact_number.trim()) newErrors.contact_number = "Contact number is required";
    if (!cityHeadData.city.length) newErrors.city = "At least one city is required";
    if (!cityHeadData.locality.length) newErrors.locality = "At least one locality is required";
    if (!cityHeadData.project.length) newErrors.project = "At least one project is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    try {
      const payload = {
        id: city_head_id,
        name: cityHeadData.name,
        email: cityHeadData.email,
        country_code: cityHeadData.country_code,
        contact_number: cityHeadData.contact_number,
        city: cityHeadData.city,
        locality: cityHeadData.locality,
        project: cityHeadData.project,
      };

      const response = await dispatch(editCityHead(payload));

      if (response?.statusCode === 200) {
        toast.success("City Head updated successfully!");
        navigate(`/franchise/${franchise_id}/city-heads`);
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || error?.message || "Failed to update city head";

      if (errorMessage.toLowerCase().includes("duplicate") || errorMessage.toLowerCase().includes("already")) {
        setErrors((prev) => ({ ...prev, email: "Email already exists" }));
        return toast.error("Email already exists");
      }

      toast.error(errorMessage);
    }
  };

  return (
    <MainLayout>
      <div className="ach-wrapper">
        <div className="ach-header">
          <div className="ach-header-left">
            <div className="ach-header-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z" />
              </svg>
            </div>
            <div>
              <h1 className="ach-title">Edit City Head</h1>
              <p className="ach-subtitle">Update details for this city head</p>
            </div>
          </div>

          <Link to={`/franchise/${franchise_id}/city-heads`} className="ach-view-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 21h18" />
              <path d="M5 21V7l7-4 7 4v14" />
            </svg>
            Back to Franchise
          </Link>
        </div>

        <form className="ach-form" onSubmit={handleSubmit}>
          <div className="ach-section">
            <div className="ach-section-header">
              <span className="ach-section-badge">01</span>
              <h2 className="ach-section-title">City Head Details</h2>
            </div>

            <div className="ach-grid ach-grid-2">
              <div className="ach-field">
                <label className="ach-label">
                  Name <span className="ach-req">*</span>
                </label>
                <input
                  className={`ach-input ${errors.name ? "ach-input--error" : ""}`}
                  type="text"
                  name="name"
                  value={cityHeadData.name}
                  onChange={handleChange}
                  placeholder="e.g. Rahul Sharma"
                  disabled={cityHeadEditing || detailLoading}
                />
                {errors.name && <span className="ach-error-msg">{errors.name}</span>}
              </div>

              <div className="ach-field">
                <label className="ach-label">
                  Email <span className="ach-req">*</span>
                </label>
                <input
                  className={`ach-input ${errors.email ? "ach-input--error" : ""}`}
                  type="email"
                  name="email"
                  value={cityHeadData.email}
                  onChange={handleChange}
                  placeholder="e.g. rahul@example.com"
                  disabled={cityHeadEditing || detailLoading}
                />
                {errors.email && <span className="ach-error-msg">{errors.email}</span>}
              </div>

              <div className="ach-field">
                <label className="ach-label">
                  Country Code <span className="ach-req">*</span>
                </label>
                <select
                  className="ach-select"
                  name="country_code"
                  value={cityHeadData.country_code}
                  onChange={handleChange}
                  disabled={cityHeadEditing || detailLoading}
                >
                  <option value="+91">India (+91)</option>
                  <option value="+1">USA (+1)</option>
                  <option value="+971">UAE (+971)</option>
                </select>
              </div>

              <div className="ach-field">
                <label className="ach-label">
                  Contact Number <span className="ach-req">*</span>
                </label>
                <input
                  className={`ach-input ${errors.contact_number ? "ach-input--error" : ""}`}
                  type="text"
                  name="contact_number"
                  value={cityHeadData.contact_number}
                  onChange={handleChange}
                  placeholder="e.g. 9876543211"
                  disabled={cityHeadEditing || detailLoading}
                />
                {errors.contact_number && <span className="ach-error-msg">{errors.contact_number}</span>}
              </div>

              <SearchableMultiSelect
                label="City"
                required
                placeholder="Search cities..."
                disabled={cityHeadEditing || detailLoading || cityOptions.length === 0}
                options={cityOptions}
                selected={cityHeadData.city}
                onChange={handleCityChange}
                error={errors.city}
                emptyMessage="No cities assigned to this franchise manager"
              />

              <div className="ach-field-with-action">
                <button
                  type="button"
                  className="ach-select-all-btn"
                  onClick={() => {
                    const availableValues = localityOptions.filter((l) => !l.disabled).map((l) => l.value);
                    const allSelected =
                      availableValues.length > 0 && availableValues.every((v) => cityHeadData.locality.includes(v));
                    handleLocalityChange(allSelected ? [] : availableValues);
                  }}
                  disabled={
                    cityHeadData.city.length === 0 ||
                    cityHeadEditing ||
                    detailLoading ||
                    localityOptions.filter((l) => !l.disabled).length === 0
                  }
                >
                  {(() => {
                    const availableValues = localityOptions.filter((l) => !l.disabled).map((l) => l.value);
                    const allSelected =
                      availableValues.length > 0 && availableValues.every((v) => cityHeadData.locality.includes(v));
                    return allSelected ? "Unselect All" : "Select All";
                  })()}
                </button>

                <SearchableMultiSelect
                  label="Locality"
                  required
                  placeholder={cityHeadData.city.length === 0 ? "Select city first" : "Search localities..."}
                  disabled={cityHeadData.city.length === 0 || cityHeadEditing || detailLoading}
                  options={localityOptions}
                  selected={cityHeadData.locality}
                  onChange={handleLocalityChange}
                  error={errors.locality}
                  emptyMessage="No localities for the selected city"
                />
              </div>

              <div className="ach-field-with-action">
                <button
                  type="button"
                  className="ach-select-all-btn"
                  onClick={() => {
                    const availableValues = projectOptions.filter((p) => !p.disabled).map((p) => p.value);
                    const allSelected =
                      availableValues.length > 0 && availableValues.every((v) => cityHeadData.project.includes(v));
                    handleProjectChange(allSelected ? [] : availableValues);
                  }}
                  disabled={
                    cityHeadData.locality.length === 0 ||
                    cityHeadEditing ||
                    detailLoading ||
                    projectOptions.filter((p) => !p.disabled).length === 0
                  }
                >
                  {(() => {
                    const availableValues = projectOptions.filter((p) => !p.disabled).map((p) => p.value);
                    const allSelected =
                      availableValues.length > 0 && availableValues.every((v) => cityHeadData.project.includes(v));
                    return allSelected ? "Unselect All" : "Select All";
                  })()}
                </button>

                <SearchableMultiSelect
                  label="Project"
                  required
                  placeholder={cityHeadData.locality.length === 0 ? "Select locality first" : "Search projects..."}
                  disabled={cityHeadData.locality.length === 0 || cityHeadEditing || detailLoading}
                  options={projectOptions}
                  selected={cityHeadData.project}
                  onChange={handleProjectChange}
                  error={errors.project}
                  emptyMessage="No projects for the selected locality"
                />
              </div>
            </div>
          </div>

          <div className="ach-footer">
            <button type="submit" className="ach-btn ach-btn--submit" disabled={cityHeadEditing || detailLoading}>
              {cityHeadEditing ? "Saving..." : "Save Changes"}
            </button>

            <Link to={`/franchise/${franchise_id}/city-heads`} className="ach-btn ach-btn--cancel">
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default EditCityHead;